import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ExternalLink, MoreHorizontal, Eye, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getSafeboxApp,
  sendSafeBoxOtp,
  safeboxApproveForm,
  safeboxRejectForm,
} from '@/utils/services/user.service';
import { getBscTxUrl } from '@/config/config';
import SignaturePad from '@/store/Layout/orders/SignaturePad';
import PdfSignature from '@/store/Layout/orders/PdfSignature';
import {
  PageWrapper,
  InnerWrapper,
  StickyHeader,
  HeaderTitle,
  CreateBtn,
  ContractGrid,
  GridHeader,
  ContractRow,
  ContractId,
  ContractName,
  StatusBadge,
  SignaturesWrap,
  SignaturesTooltip,
  UpdatedDate,
  ViewBtn,
  ApproveBtn,
  RejectBtn,
  ActionsCol,
  HashLink,
  HashCell,
  TypeCell,
  CellText,
  ApprovalSidebar,
  SidebarHeader,
  SidebarCloseBtn,
  LoadingText,
  SidebarDetailTitle,
  SidebarDetailSection,
  SidebarDetailRow,
  SidebarDetailLabel,
  SidebarDetailValue,
  SidebarHashLink,
  SidebarHashList,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  OTPInput,
  OTPProgressBar,
  OTPProgressFill,
  ModalActions,
  BtnGhost,
  BtnPrimary,
  FormLabel,
  FormGroup,
  ActionsMenuButton,
  ActionsMenu,
  ActionsMenuItem,
} from './ContractList.styled';

function mapSafeboxToContract(item) {
  const approversList = item.approvers?.filter((a) => a && (a.approver || a.status)) || [];
  const totalApprovers = approversList.length || 1;
  const approved = approversList.filter((a) => a.status === 'Accepted').length || 0;
  const statusMap = { Waiting: 'Pending', Completed: 'Approved', Rejected: 'Rejected' };
  const firstHash = item.hash?.[0];
  return {
    id: item._id,
    _id: item._id,
    name: item.name || 'Untitled',
    counterparty: item.owner?.name || item.owner?.email || '—',
    status: statusMap[item.status] || item.status,
    signatures: `${approved}/${totalApprovers}`,
    updated: item.date || '—',
    contractType: item.contract_type || 'Safebox',
    amount: item.amount ?? '—',
    approver: item.owner?.name || item.owner?.email || '—',
    firstHash,
    raw: item,
  };
}

function isCurrentUserApprover(contract, userAddress) {
  if (!userAddress) return false;
  const addr = String(userAddress).toLowerCase();
  const approvers = contract?.approvers || [];
  return approvers.some(
    (a) => a?.approver?.address && String(a.approver.address).toLowerCase() === addr
  );
}

export default function ContractList() {
  const [selected, setSelected] = useState(null);

  const [contracts, setContracts] = useState([]);
  console.log("🚀 ~ ContractList ~ contracts:", contracts)
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [otpModal, setOtpModal] = useState({
    open: false,
    action: null,
    contractId: null,
  });
  const [otp, setOtp] = useState('');
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureImageFile, setSignatureImageFile] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const OTP_COOLDOWN = 60;
  const [otpRemaining, setOtpRemaining] = useState(OTP_COOLDOWN);
  const [openActionsId, setOpenActionsId] = useState(null);

  useEffect(() => {
    if (!otpModal.open) return;
    setOtpRemaining(OTP_COOLDOWN);
    const t = setInterval(() => {
      setOtpRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [otpModal.open]);

  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleSignatureEnd = (dataUrl) => {
    const blob = dataURLtoBlob(dataUrl);
    const fileName = 'signature.png';
    const fileType = 'image/png';
    const file = new File([blob], fileName, { type: fileType });
    setSignatureImageFile(file);
  };

  const getPDf = (value) => {
    setSignatureFile(value);
  };

  const getPDf1 = () => {
    setSignatureFile(null);
  };

  const fetchContracts = useCallback(() => {
    getSafeboxApp({ limit: 100, skip: 0 })
      .then((res) => {
        const data = res?.data?.data;
        setContracts(Array.isArray(data) ? data.map(mapSafeboxToContract) : []);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load contracts');
        setContracts([]);
      })
      .finally(() => setLoading(false));
  }, []);
  const currentUserAddress =
    JSON.parse(localStorage.getItem('user') || '{}')?.data?.user?.address || '';

  useEffect(() => {
    setLoading(true);
    fetchContracts();
  }, [fetchContracts]);

  const openOtpModal = useCallback((action, contract) => {
    setOtpModal({ open: true, action, contractId: contract.id, contract });
    setOtp('');
    setSignatureFile(null);
    setSignatureImageFile(null);
  }, []);

  const closeOtpModal = useCallback(() => {
    setOtpModal({ open: false, action: null, contractId: null, contract: null });
    setOtp('');
    setSignatureFile(null);
    setSignatureImageFile(null);
  }, []);

  const handleApprove = useCallback(
    (contract) => {
      const contractId = contract.id;
      setApprovingId(contractId);
      sendSafeBoxOtp()
        .then(() => {
          openOtpModal('approve', contract);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message || 'Failed to send OTP');
        })
        .finally(() => setApprovingId(null));
    },
    [openOtpModal]
  );

  const handleReject = useCallback(
    (contract) => {
      const contractId = contract.id;
      setRejectingId(contractId);
      sendSafeBoxOtp()
        .then(() => {
          openOtpModal('reject', contract);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message || 'Failed to send OTP');
        })
        .finally(() => setRejectingId(null));
    },
    [openOtpModal]
  );

  const handleOtpConfirm = useCallback(() => {
    const { action, contractId } = otpModal;
    if (!contractId || !otp?.trim()) {
      toast.error('Please enter OTP');
      return;
    }
    if (!signatureFile) {
      toast.error('Please sign the document before confirming');
      return;
    }
    setOtpLoading(true);
    const formData = new FormData();
    formData.append('id', contractId);
    formData.append('otp', otp.trim());
    formData.append('File', signatureFile);
    const apiCall = action === 'approve' ? safeboxApproveForm : safeboxRejectForm;
    apiCall(formData)
      .then(() => {
        toast.success(action === 'approve' ? 'Contract approved' : 'Contract rejected');
        closeOtpModal();
        fetchContracts();
        if (selected?.id === contractId) setSelected(null);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to confirm');
      })
      .finally(() => setOtpLoading(false));
  }, [otpModal, otp, signatureFile, closeOtpModal, fetchContracts, selected?.id]);

  const handleResendOtp = useCallback(() => {
    setOtpRemaining(OTP_COOLDOWN);
    sendSafeBoxOtp()
      .then((response) => {
        toast.success(response?.data?.message || 'OTP resent to your email');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to resend OTP');
      });
  }, []);

  return (
    <PageWrapper>
      <InnerWrapper>
        <StickyHeader>
          <HeaderTitle>Contracts</HeaderTitle>
          <CreateBtn as={Link} to="/new-safebox">
            <Plus size={16} />
            Create Safebox
          </CreateBtn>
        </StickyHeader>

        <ContractGrid>
          {loading ? (
            <LoadingText>Loading…</LoadingText>
          ) : contracts.length === 0 ? (
            <LoadingText>No record found</LoadingText>
          ) : (
            <>
              <GridHeader>
                <span>ID</span>
                <span>Name</span>
                <span>Date</span>
                <span>Amount</span>
                <span>Approver</span>
                <span>Hash</span>
                <span>Type</span>
                <span>Signatures</span>
                <span>Status</span>
                <span>Action</span>
              </GridHeader>
              {contracts.map((c) => {
                const isFinalized = c.status === 'Approved' || c.status === 'Rejected';
                const isPending = c.status === 'Pending';

                return (
                    <ContractRow key={c.id} $pending={isPending}>
                    <ContractId>
                      {(() => {
                        const idStr = String(c.id || '');
                        if (idStr.length <= 12) return idStr;
                        return `${idStr.slice(0, 6)}…${idStr.slice(-4)}`;
                      })()}
                    </ContractId>
                    <div>
                      <ContractName>{c.name}</ContractName>
                    </div>
                    <UpdatedDate>{c.updated}</UpdatedDate>
                    <CellText>{c.amount}</CellText>
                    <CellText>{c.approver}</CellText>
                    <HashCell>
                      {c.firstHash ? (
                        <HashLink
                          href={getBscTxUrl(c.firstHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink size={14} />
                          {/* {String(c.firstHash).slice(0, 10)}… */}
                        </HashLink>
                      ) : (
                        <CellText as="span">—</CellText>
                      )}
                    </HashCell>
                    <TypeCell>
                      <CellText>{c.contractType}</CellText>
                    </TypeCell>
                    <SignaturesWrap>
                      <span>{c.signatures}</span>
                      <SignaturesTooltip>
                        {c.signatures.split('/')[0]} of {c.signatures.split('/')[1]} required
                        approvals completed
                      </SignaturesTooltip>
                    </SignaturesWrap>
                    <div>
                      <StatusBadge $status={c.status}>
                        {c.status}
                      </StatusBadge>
                    </div>
                    <ActionsCol>
                      <ActionsMenuButton
                        type="button"
                        aria-label="Open actions"
                        onClick={() =>
                          setOpenActionsId((prev) => (prev === c.id ? null : c.id))
                        }
                      >
                        <MoreHorizontal size={18} />
                      </ActionsMenuButton>
                      {openActionsId === c.id && (
                        <ActionsMenu>
                          <ActionsMenuItem
                            type="button"
                            onClick={() => {
                              setSelected(c);
                              setOpenActionsId(null);
                            }}
                          >
                            <Eye size={16} />
                            <span>View</span>
                          </ActionsMenuItem>
                          {!isFinalized &&
                            isCurrentUserApprover(c.raw, currentUserAddress) && (
                              <>
                                <ActionsMenuItem
                                  type="button"
                                  disabled={approvingId === c.id}
                                  onClick={() => {
                                    handleApprove(c);
                                    setOpenActionsId(null);
                                  }}
                                >
                                  <CheckCircle2 size={16} />
                                  <span>{approvingId === c.id ? 'Approving…' : 'Approve'}</span>
                                </ActionsMenuItem>
                                <ActionsMenuItem
                                  type="button"
                                  disabled={rejectingId === c.id}
                                  onClick={() => {
                                    handleReject(c);
                                    setOpenActionsId(null);
                                  }}
                                >
                                  <XCircle size={16} />
                                  <span>{rejectingId === c.id ? 'Rejecting…' : 'Reject'}</span>
                                </ActionsMenuItem>
                              </>
                            )}
                        </ActionsMenu>
                      )}
                    </ActionsCol>
                  </ContractRow>
                );
              })}
            </>
          )}
        </ContractGrid>
      </InnerWrapper>

      {selected && selected.raw && (
        <ApprovalSidebar>
          <SidebarHeader>
            <span>Contract Details</span>
            <SidebarCloseBtn type="button" onClick={() => setSelected(null)}>
              Close
            </SidebarCloseBtn>
          </SidebarHeader>

          <SidebarDetailTitle>{selected.raw.name || 'Untitled'}</SidebarDetailTitle>

          <SidebarDetailSection>
            <SidebarDetailRow>
              <SidebarDetailLabel>Date</SidebarDetailLabel>
              <SidebarDetailValue>{selected.raw.date || '—'}</SidebarDetailValue>
            </SidebarDetailRow>
            <SidebarDetailRow>
              <SidebarDetailLabel>Status</SidebarDetailLabel>
              <SidebarDetailValue>{selected.raw.status || '—'}</SidebarDetailValue>
            </SidebarDetailRow>
            <SidebarDetailRow>
              <SidebarDetailLabel>Type</SidebarDetailLabel>
              <SidebarDetailValue>{selected.raw.contract_type || '—'}</SidebarDetailValue>
            </SidebarDetailRow>
            <SidebarDetailRow>
              <SidebarDetailLabel>Your Role</SidebarDetailLabel>
              <SidebarDetailValue>{selected.raw.current_user_role || '—'}</SidebarDetailValue>
            </SidebarDetailRow>
          </SidebarDetailSection>

          {selected.raw.owner && (
            <SidebarDetailSection>
              <SidebarDetailLabel>Owner</SidebarDetailLabel>
              <SidebarDetailRow>
                <SidebarDetailValue>{selected.raw.owner.name || selected.raw.owner.email || '—'}</SidebarDetailValue>
              </SidebarDetailRow>
              {selected.raw.owner.email && (
                <SidebarDetailRow>
                  <SidebarDetailValue>{selected.raw.owner.email}</SidebarDetailValue>
                </SidebarDetailRow>
              )}
            </SidebarDetailSection>
          )}

          {selected.raw.files && selected.raw.files.length > 0 && (
            <SidebarDetailSection>
              <SidebarDetailLabel>Files</SidebarDetailLabel>
              {selected.raw.files.map((fileUrl, idx) => (
                <SidebarDetailRow key={idx}>
                  <SidebarHashLink
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`File ${idx + 1}`}
                  </SidebarHashLink>
                </SidebarDetailRow>
              ))}
            </SidebarDetailSection>
          )}

          {selected.raw.approvers && selected.raw.approvers.length > 0 && (
            <SidebarDetailSection>
              <SidebarDetailLabel>Approvers</SidebarDetailLabel>
              {selected.raw.approvers.map((a, i) => (
                <SidebarDetailRow key={i}>
                  <SidebarDetailValue>
                    {a.approver?.name || a.approver?.email || '—'} ({a.status || '—'})
                  </SidebarDetailValue>
                </SidebarDetailRow>
              ))}
            </SidebarDetailSection>
          )}

          {selected.raw.hash && selected.raw.hash.length > 0 && (
            <SidebarDetailSection>
              <SidebarDetailLabel>Transaction Hash</SidebarDetailLabel>
              <SidebarHashList>
                {selected.raw.hash.map((h, i) => (
                  <SidebarHashLink
                    key={i}
                    href={getBscTxUrl(h)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {h}
                  </SidebarHashLink>
                ))}
              </SidebarHashList>
            </SidebarDetailSection>
          )}
        </ApprovalSidebar>
      )}

      {otpModal.open && (
        <ModalOverlay onClick={closeOtpModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              Enter OTP to {otpModal.action === 'approve' ? 'Approve' : 'Reject'} Contract
            </ModalTitle>
            <OTPInput
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <OTPProgressBar>
              <OTPProgressFill
                style={{
                  width: `${(otpRemaining / OTP_COOLDOWN) * 100}%`,
                }}
              />
            </OTPProgressBar>
            <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 16 }}>
              {otpRemaining > 0
                ? `Resend available in ${otpRemaining}s`
                : 'You can resend the OTP'}
            </p>
            <FormGroup>
              <FormLabel>Sign contract document (required)</FormLabel>
              {otpModal.contract?.raw?.lot_status ? (
                <>
                  {otpModal.contract?.raw?.files?.[0] && (
                    <a
                      href={otpModal.contract.raw.files[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        fontSize: 12,
                        marginBottom: 8,
                        textDecoration: 'underline',
                      }}
                    >
                      View original document
                    </a>
                  )}
                  {otpModal.contract?.raw?.files?.[0] ? (
                    <>
                      <div
                        style={{
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 12,
                          padding: 12,
                          marginTop: 4,
                        }}
                      >
                        <SignaturePad getPDf1={getPDf1} onEnd={handleSignatureEnd} />
                        {signatureImageFile && (
                          <PdfSignature
                            pdfFile={otpModal.contract.raw.files[0]}
                            signatureImage={signatureImageFile}
                            getPDf={getPDf}
                          />
                        )}
                      </div>
                      {signatureFile && (
                        <span
                          style={{
                            fontSize: 12,
                            opacity: 0.8,
                            marginTop: 4,
                            display: 'block',
                          }}
                        >
                          {signatureFile.name}
                        </span>
                      )}
                    </>
                  ) : (
                    <LoadingText>No document found for this safebox</LoadingText>
                  )}
                </>
              ) : (
                <>
                  {otpModal.contract?.raw?.files?.[0] && (
                    <a
                      href={otpModal.contract.raw.files[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        fontSize: 12,
                        marginBottom: 8,
                        textDecoration: 'underline',
                      }}
                    >
                      View existing document
                    </a>
                  )}
                  <input
                    type="file"
                    accept=".pdf,application/pdf,image/*"
                    onChange={(e) => setSignatureFile(e.target.files?.[0] || null)}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 12,
                      color: '#fff',
                      fontSize: 14,
                    }}
                  />
                  {signatureFile && (
                    <span
                      style={{
                        fontSize: 12,
                        opacity: 0.8,
                        marginTop: 4,
                        display: 'block',
                      }}
                    >
                      {signatureFile.name}
                    </span>
                  )}
                </>
              )}
            </FormGroup>
            <ModalActions style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <BtnGhost type="button" onClick={closeOtpModal}>
                  Cancel
                </BtnGhost>
                <BtnGhost
                  type="button"
                  disabled={otpRemaining > 0}
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </BtnGhost>
              </div>
              <BtnPrimary
                type="button"
                onClick={handleOtpConfirm}
                disabled={!otp?.trim() || !signatureFile || otpLoading}
              >
                {otpLoading ? 'Confirming…' : 'Confirm'}
              </BtnPrimary>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
}
